import { useRef, useMemo, useEffect } from "react";
import { init } from "@swiggy-private/native-bridge";
import Blaze from "@swiggy-private/blaze.js";
import { initBridgeSdk } from "utils/bridgeSdk";

import { createNBRequestInterceptor, createNBResponseInterceptor } from "utils/NativeBridgeInterceptor";
import { allowMetricCapture, getAppHost, getUserAgent } from "utils/utils";
import WebBridgeSdk from "utils/webBridgeSdk";
import packageJson from "../package.json";
import { useEffectOnce } from "./useEffectOnce";

// initalise Native Bridge SDK
// Wrap in try-catch to handle test environment issues
let Sdk: ReturnType<typeof init> | undefined;
try {
    Sdk = init();
    WebBridgeSdk.setUpWebSdk();
} catch (error) {
    console.warn("[useBootstrap] Failed to initialize SDK:", error);
    Sdk = undefined;
}

export { Sdk };

const EMPTY_FUNCTION = () => void 0;

export const blazeInit = (nbEventTypePrefix: string) => {
    Blaze.init({
        sendToAnalytics: EMPTY_FUNCTION,
        enableResourceTiming: false,
        nrConfig: {
            enable: true,
            config: {
                NR_ENDPOINT: process.env.NEXT_PUBLIC_NEW_RELIC_ENDPOINT ?? "",
                NR_KEY: process.env.NEXT_PUBLIC_NEW_RELIC_KEY ?? "",
            },
            meta: {
                appName: nbEventTypePrefix,
                /* JS hash is generated without replacing with actual value
                So cached file would be same for both canary vs non-canary, and would have unknown behavior */
                appDeployName: (document.querySelector("[data-app-name]") as HTMLDivElement)?.dataset.appName ?? "",
                appHostName: getAppHost(),
                appEnvName: process.env.NEXT_PUBLIC_SHUTTLE_ENV ?? "",
                userAgent: getUserAgent(),
                appVersion: packageJson.version,
            },
        },
    });
};

export function useBootstrap(nbEventTypePrefix: string): void {
    const initRef = useRef(false);

    if (!initRef.current && allowMetricCapture()) {
        // init blaze
        blazeInit(nbEventTypePrefix);
        // create function to intialise NB
        initBridgeSdk(Sdk);
    }
    const requestInterceptor = useMemo(
        () => createNBRequestInterceptor(Blaze.recordCustomEvent, nbEventTypePrefix),
        [nbEventTypePrefix],
    );
    const responseInterceptor = useMemo(
        () => createNBResponseInterceptor(Blaze.recordCustomEvent, nbEventTypePrefix),
        [nbEventTypePrefix],
    );
    useEffectOnce(() => {
        initRef.current = true;
    });

    useEffect(() => {
        return (): void => {
            requestInterceptor();
            responseInterceptor();
        };
    }, [requestInterceptor, responseInterceptor]);
}
